import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import { IPaymentMethod, IReloadPrepaidCard } from 'features/payment/types';
import { isApplePay, isGooglePay } from 'features/payment/utils';

import { IServerOrder } from '@rbi-ctg/menu';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';

import { useRequestApplePayPayment } from './use-request-apple-pay-payment';
import { useRequestGooglePayPayment } from './use-request-google-pay-payment';

const getPrepaidBalance = (prepaidPaymentMethod: IPaymentMethod | undefined) => {
  if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
    if (prepaidPaymentMethod.prepaid.feFormattedCurrentBalance) {
      return prepaidPaymentMethod.prepaid.feFormattedCurrentBalance;
    } else if (prepaidPaymentMethod.prepaid.currentBalance) {
      return prepaidPaymentMethod.prepaid.currentBalance;
    }
  }
  return 0;
};

export const useFirstDataPrepaidReload = ({
  serverOrder,
  reloadAmount,
  billingCountry,
}: {
  serverOrder: IServerOrder | null;
  reloadAmount: number;
  billingCountry: ISOs;
}) => {
  const {
    reloadPrepaidCard,
    checkoutPaymentMethod,
    prepaidReloadPaymentMethodId,
  } = usePaymentContext();
  const [isReloadLoading, setIsReloadLoading] = useState(false);

  const reloadWithApplePay = isApplePay(prepaidReloadPaymentMethodId);
  const reloadWithGooglePay = isGooglePay(prepaidReloadPaymentMethodId);

  const prepaidBalance = getPrepaidBalance(checkoutPaymentMethod);
  const { countryCode } = getCountryAndCurrencyCodes(billingCountry);
  const prepaidFdAccountId =
    checkoutPaymentMethod?.accountIdentifier ?? checkoutPaymentMethod?.fdAccountId ?? '';

  const args: IReloadPrepaidCard = useMemo(
    () => ({
      countryCode,
      fundValue: reloadAmount,
      prepaidBalance,
      prepaidFdAccountId,
      fdAccountId: prepaidReloadPaymentMethodId,
    }),
    [countryCode, prepaidBalance, prepaidFdAccountId, prepaidReloadPaymentMethodId, reloadAmount]
  );

  const {
    requestPayment: requestGooglePayPayment,
    isLoading: isGooglePayLoading,
    data: googlePayPaymentDetails,
  } = useRequestGooglePayPayment({
    googlePay: {
      billingCountry,
      total: 0,
      reloadAmount,
      isForPrepaidReload: true,
    },
  });
  const {
    requestPayment: requestApplePayPayment,
    isLoading: isApplePayLoading,
    data: applePayPaymentDetails,
  } = useRequestApplePayPayment({
    applePay: {
      serverOrder,
      tip: 0,
      reloadAmount,
      billingCountry,
      isForPrepaidReload: true,
    },
  });

  useEffect(() => {
    async function handleReload() {
      if (reloadAmount === 0) {
        return;
      }
      setIsReloadLoading(true);
      if (applePayPaymentDetails?.applePayDetails) {
        await reloadPrepaidCard({
          ...args,
          applePayDetails: applePayPaymentDetails.applePayDetails,
        });
      }

      if (googlePayPaymentDetails?.googlePayDetails) {
        await reloadPrepaidCard({
          ...args,
          googlePayDetails: googlePayPaymentDetails?.googlePayDetails,
        });
      }

      setIsReloadLoading(false);
    }
    handleReload();
  }, [
    args,
    applePayPaymentDetails?.applePayDetails,
    googlePayPaymentDetails?.googlePayDetails,
    reloadAmount,
    reloadPrepaidCard,
  ]);

  const reloadPrepaid = useCallback(async () => {
    if (reloadAmount === 0) {
      return;
    }
    if (reloadWithApplePay) {
      requestApplePayPayment();
    }

    if (reloadWithGooglePay) {
      requestGooglePayPayment();
    }

    setIsReloadLoading(true);

    await reloadPrepaidCard(args);
    // TODO: logs here?

    setIsReloadLoading(false);
  }, [
    args,
    reloadAmount,
    reloadPrepaidCard,
    reloadWithApplePay,
    reloadWithGooglePay,
    requestApplePayPayment,
    requestGooglePayPayment,
  ]);

  return { isLoading: isReloadLoading || isApplePayLoading || isGooglePayLoading, reloadPrepaid };
};
